import React, { useState, useContext, useEffect, useMemo, useRef } from 'react'
import LanguageContext from '../../../Store/LanguageContext'
import MultipleAssessmentsContext from '../../../Store/MultipleAssessmentsContext'
import { translate } from '../../translate/translate'
import { CircularProgress } from '@mui/material'

const MatrixMultipleAssessments = ({ assessments = null }) => {
  const langCtx = useContext(LanguageContext)
  const multipleAssessmentsCtx = useContext(MultipleAssessmentsContext)
  const { deployments, deploymentInfo, updateSelectedDeploymentIds, selectedDeploymentIds } = multipleAssessmentsCtx
  const [isLoading, setIsLoading] = useState(true)
  const headerScrollRef = useRef(null)
  const bodyScrollRef = useRef(null)

  const mapDeploymentsToAssessments = () => {
    if (!deploymentInfo || !deploymentInfo.data) return []
    const { deployments: deploymentList, sections } = deploymentInfo.data
    if (!deploymentList || deploymentList.length === 0) {
      const noDeploymentSection = sections?.find(section => section.deploymentId === "No Deployment")
      if (noDeploymentSection) {
        const leaders = noDeploymentSection.designations?.find(d => d.designation === "leader")?.count || 0
        const parishioners = noDeploymentSection.designations?.find(d => d.designation === "parishioner")?.count || 0
        const priests = noDeploymentSection.designations?.find(d => d.designation === "priest")?.count || 0
        
        return [{
          id: "MW1",
          name: "MW1",
          date: "-",
          leaders: leaders,
          priests: priests,
          parishioners: parishioners,
          status: "active",
          isSelected: false,
          isChecked: false
        }]
      }
      return []
    }

    return deploymentList.slice().reverse().map((deployment, index) => {
      const isFirstDeployment = index === 0 
      const section = sections?.find(s => s.deploymentId === deployment.deploymentId)
      
      const leaders = section?.designations?.find(d => d.designation === "leader")?.count || 0
      const priests = section?.designations?.find(d => d.designation === "priest")?.count || 0
      const parishioners = section?.designations?.find(d => d.designation === "parishioner")?.count || 0
      
      return {
        id: deployment.deploymentId,
        name: deployment.deploymentTitle,
        date: deployment.createdat ? new Date(deployment.createdat).toLocaleDateString('en-US', { 
          year: 'numeric', 
          month: 'long', 
          day: 'numeric' 
        }) : "-",
        leaders: leaders,
        priests: priests,
        parishioners: parishioners,
        status: isFirstDeployment ? "active" : "inactive",
        isSelected: false,
        isChecked: false
      }
    })
  }

  const handleTranslate = (text, type) => {
    switch (langCtx.language) {
      case "Nederlands": {
        return translate(text, "dashboardCompareDutch", type)
      }
      default: {
        return text;
      }
    }
  }
  const assessmentsData = useMemo(() => {
    return assessments || mapDeploymentsToAssessments()
  }, [assessments, deploymentInfo])

  useEffect(() => {
    if (assessments !== null || (deploymentInfo && deploymentInfo.data)) {
      setIsLoading(false)
    } else {
      setIsLoading(true)
    }
  }, [assessments, deploymentInfo])

  // Sync horizontal scrolling between header and body
  useEffect(() => {
    if (isLoading) return

    let cleanup = null
    let timeoutId = null
    let retryTimeoutId = null

    const setupScrollSync = () => {
      const headerElement = headerScrollRef.current
      const bodyElement = bodyScrollRef.current

      if (!headerElement || !bodyElement) {
        retryTimeoutId = setTimeout(setupScrollSync, 100)
        return
      }

      let syncingFromBody = false
      let syncingFromHeader = false

      const handleBodyScroll = () => {
        if (!syncingFromHeader && headerElement) {
          syncingFromBody = true
          headerElement.scrollLeft = bodyElement.scrollLeft
          requestAnimationFrame(() => {
            syncingFromBody = false
          })
        }
      }

      const handleHeaderScroll = () => {
        if (!syncingFromBody && bodyElement) {
          syncingFromHeader = true
          bodyElement.scrollLeft = headerElement.scrollLeft
          requestAnimationFrame(() => {
            syncingFromHeader = false
          })
        }
      }

      bodyElement.addEventListener('scroll', handleBodyScroll, { passive: true })
      headerElement.addEventListener('scroll', handleHeaderScroll, { passive: true })

      cleanup = () => {
        bodyElement.removeEventListener('scroll', handleBodyScroll)
        headerElement.removeEventListener('scroll', handleHeaderScroll)
      }
    }

    // Small delay to ensure DOM is fully rendered after page refresh
    timeoutId = setTimeout(setupScrollSync, 200)
    
    return () => {
      if (timeoutId) clearTimeout(timeoutId)
      if (retryTimeoutId) clearTimeout(retryTimeoutId)
      if (cleanup) cleanup()
    }
  }, [isLoading, assessmentsData])

  const handleCheckboxChange = (assessmentId) => {
    const currentSelections = selectedDeploymentIds || []
    let newSelection
    
    if (currentSelections.includes(assessmentId)) {
      newSelection = currentSelections.filter(id => id !== assessmentId)
    } else if (currentSelections.length >= 2) {
      newSelection = [currentSelections[1], assessmentId]
    } else {
      newSelection = [...currentSelections, assessmentId]
    }
    
    updateSelectedDeploymentIds(newSelection)
  }

  const getRowClassName = (assessment) => {
    const isSelected = (selectedDeploymentIds || []).includes(assessment.id)
    
    if (isSelected) {
      return assessment.status === "active" 
        ? "table-row selected-active-row" 
        : "table-row selected-inactive-row"
    }
    return "table-row default-row"
  }

  const getAssessmentClassName = (assessment) => {
    if (assessment.status === "active") return "assessment-name active-assessment"
    if (assessment.status === "selected") return "assessment-name selected-assessment"
    return "assessment-name inactive-assessment"
  }

  const getDateClassName = (assessment) => {
    if (assessment.status === "active") return "date-text active-date"
    if (assessment.status === "selected") return "date-text selected-date"
    return "date-text inactive-date"
  }

  const getLeadersClassName = (assessment) => {
    if (assessment.status === "active") return "leaders-text active-leaders"
    if (assessment.status === "selected") return "leaders-text selected-leaders"
    return "leaders-text inactive-leaders"
  }

  const getPriestsClassName = (assessment) => {
    if (assessment.status === "active") return "priests-text active-priests"
    if (assessment.status === "selected") return "priests-text selected-priests"
    return "priests-text inactive-priests"
  }

  const getParishionersClassName = (assessment) => {
    if (assessment.status === "active") return "parishioners-text active-parishioners"
    if (assessment.status === "selected") return "parishioners-text selected-parishioners"
    return "parishioners-text inactive-parishioners"
  }

  return (
    <div className="assessments-table">
      {isLoading ? (
        <div className="loader-container">
          <CircularProgress style={{ color: '#792122' }} size={50} />
        </div>
      ) : (
        <>
          <div className="table-header-wrapper" ref={headerScrollRef}>
            <table className="assessments-table-header">
              <thead>
                <tr className="table-header">
                  <th style={{ paddingLeft: '19px' }} className="header-cell assessments-column">
                    <p style={{margin: 0, marginLeft:'37px'}}>{handleTranslate("Assessments", "assessments")}</p></th>
                  <th className="header-cell date-column">{handleTranslate("Date", "date")}</th>
                  <th className="header-cell leaders-column">{handleTranslate("Leaders", "leaders")}</th>
                  <th className="header-cell parishioners-column">{handleTranslate("Parishioners", "parishioners")}</th>
                </tr>
              </thead>
            </table>
          </div>
          <div className="table-body-wrapper" ref={bodyScrollRef}>
            <table className="assessments-table-content">
              <tbody className='table-body'>
                {assessmentsData.map((assessment) => (
                  <tr key={assessment.id} className={getRowClassName(assessment)}>
                    <td className="data-cell assessments-cell">
                      <div className="assessment-item">
                        <input 
                          type="checkbox" 
                          className="assessment-checkbox" 
                          checked={(selectedDeploymentIds || []).includes(assessment.id)}
                          onChange={() => handleCheckboxChange(assessment.id)}
                          disabled={false}
                        />
                        <span className={getAssessmentClassName(assessment)}>{assessment?.name}</span>
                        {assessment.status === "active" && (
                          <span className="status-tag active-tag">{handleTranslate("Active", "active")}</span>
                        )}
                      </div>
                    </td>
                    <td className="data-cell date-cell">
                      <span className={getDateClassName(assessment)}>{assessment.date}</span>
                    </td>
                    <td className="data-cell leaders-cell">
                      <span className={getLeadersClassName(assessment)}>
                        {assessment.leaders + assessment.priests}
                      </span>
                    </td>
                    <td className="data-cell parishioners-cell">
                      <span className={getParishionersClassName(assessment)}>{assessment.parishioners}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  )
}

export default MatrixMultipleAssessments